import { icon, refreshIcons } from '../icons';
import { listTransactions, addExpensesBulk } from '@/storage/repo';
import { buildSuggestions, type ProposedExpense } from '@/suggest/buildSuggestions';
import { CATEGORY_BADGE, FALLBACK_BADGE, formatZAR } from '../format';
import { appState } from '../state';

export function renderSuggest(root: HTMLElement) {
  root.innerHTML = `
    <main class="max-w-4xl mx-auto px-4 sm:px-6 py-10 space-y-6">
      <div class="flex items-center gap-3">
        ${icon('sparkles', 'w-5 h-5 text-violet-500')}
        <h2 class="text-xl font-semibold">Suggested budget from your statements</h2>
      </div>
      <div id="suggestBody" class="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white/60 dark:bg-slate-900/60 p-6 text-sm text-slate-500">
        Loading…
      </div>
    </main>
  `;
  refreshIcons();
  void runSuggest();
}

async function runSuggest() {
  const body = document.getElementById('suggestBody')!;
  const txs = await listTransactions();
  if (txs.length === 0) {
    body.innerHTML = `
      <div class="text-center py-8">
        <div class="inline-flex w-12 h-12 items-center justify-center rounded-xl bg-violet-100 dark:bg-violet-900/30 text-violet-600 dark:text-violet-300 mb-3">${icon('upload-cloud', 'w-6 h-6')}</div>
        <p class="font-medium text-slate-700 dark:text-slate-200">No statement data yet</p>
        <p class="text-sm text-slate-500 dark:text-slate-400 mt-1">Import a Capitec or TymeBank statement to get suggestions.</p>
        <a href="#/upload" class="inline-block mt-4 px-4 py-2 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold">Upload statement</a>
      </div>
    `;
    refreshIcons();
    return;
  }

  const proposals = buildSuggestions(txs);
  const currentMonth = appState.get().currentMonth;

  if (proposals.length === 0) {
    body.innerHTML = `
      <p class="text-center py-6">${txs.length} transactions imported, but no recurring merchants detected in the last 90 days.</p>
      <p class="text-center text-xs text-slate-400">Try importing a longer history (≥2 months) for suggestions to appear.</p>
    `;
    return;
  }

  const includedSet = new Set<string>();   // names user is about to add
  const render = () => {
    body.innerHTML = `
      <p class="text-sm text-slate-500 dark:text-slate-400 mb-4">
        ${proposals.length} recurring merchant${proposals.length === 1 ? '' : 's'} detected in the last 90 days.
        Tick the ones to add to your <strong>${currentMonth}</strong> budget.
      </p>
      <ul class="divide-y divide-slate-100 dark:divide-slate-800">
        ${proposals.map((p, i) => proposalRow(p, i)).join('')}
      </ul>
      <div class="mt-5 flex items-center gap-3">
        <button id="addSelectedBtn" class="px-4 py-2 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold">Add selected to ${currentMonth}</button>
        <button id="selectStableBtn" class="px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 text-sm hover:bg-slate-100 dark:hover:bg-slate-800">Select all stable</button>
        <span id="addStatus" class="text-xs text-slate-500"></span>
      </div>
    `;
    refreshIcons();

    document.querySelectorAll<HTMLInputElement>('.js-include').forEach(cb => {
      cb.addEventListener('change', () => {
        if (cb.checked) includedSet.add(cb.value); else includedSet.delete(cb.value);
      });
    });

    document.getElementById('selectStableBtn')?.addEventListener('click', () => {
      document.querySelectorAll<HTMLInputElement>('.js-include').forEach((cb, i) => {
        if (proposals[i].stable) { cb.checked = true; includedSet.add(cb.value); }
      });
    });

    document.getElementById('addSelectedBtn')?.addEventListener('click', async () => {
      const items = proposals.filter(p => includedSet.has(p.name)).map(p => ({
        ym: currentMonth,
        name: p.name,
        category: p.category,
        amount: p.amount,
        notes: `Suggested from ${p.occurrences} statement row${p.occurrences === 1 ? '' : 's'}`,
        source: 'suggested',
      }));
      if (items.length === 0) return;
      await addExpensesBulk(items);
      const status = document.getElementById('addStatus')!;
      status.textContent = `Added ${items.length} item${items.length === 1 ? '' : 's'} to ${currentMonth}.`;
      includedSet.clear();
    });
  };

  render();
}

function proposalRow(p: ProposedExpense, i: number): string {
  const badge = CATEGORY_BADGE[p.category] || FALLBACK_BADGE;
  const stableLabel = p.stable
    ? `<span class="text-emerald-600 dark:text-emerald-400">stable</span>`
    : `<span class="text-amber-600 dark:text-amber-400">variable (±${(p.stdDev * 100).toFixed(0)}%)</span>`;
  return `
    <li class="py-3 flex items-start gap-3">
      <input type="checkbox" class="js-include nice-check mt-1" value="${escapeHtml(p.name)}" id="prop-${i}" />
      <label for="prop-${i}" class="flex-1 min-w-0 cursor-pointer">
        <div class="flex items-baseline gap-2 flex-wrap">
          <span class="font-medium">${escapeHtml(p.name)}</span>
          <span class="px-2 py-0.5 text-[10px] font-semibold rounded-full ${badge}">${escapeHtml(p.category)}</span>
        </div>
        <div class="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
          ${p.occurrences}× in last 90 days · median ${formatZAR(p.median)} · ${stableLabel}
        </div>
      </label>
      <div class="text-right shrink-0">
        <div class="font-semibold tabular-nums">${formatZAR(p.amount)}</div>
        <div class="text-[10px] text-slate-400">per month (mean)</div>
      </div>
    </li>
  `;
}

function escapeHtml(s: string) {
  return String(s ?? '').replace(/[&<>"']/g, c => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
  }[c]!));
}
